// ✅ Fichier : src/pages/FactureFinale.jsx
import React, { useEffect, useMemo, useRef, useState, useContext, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import html2pdf from "html2pdf.js";
import html2canvas from "html2canvas";
import axiosInstance from "../axiosInstance";
import FooterApercu from "../components/FooterApercu";
import TableauStyleVertical from "../components/TableauStyleVertical";
import { SocieteContext } from "../context/SocieteContext";
import BoutonOnglet from "../components/BoutonOnglet";
import DOMPurify from "dompurify";
import { hydrateProtectedHtml, revokeAllProtectedUrls } from "../utils/protectedFiles";
import { notify } from "../ui/notify";
import { buildPdfBlobWithLastFooterAndPages } from "../utils/pdf";
import UnsavedDialog from "../components/UnsavedDialog";
import { loadTableTheme, themeStyleVars, onThemeChange } from "../utils/tableTheme";
/* ---------------- Helpers ---------------- */
const r2 = (n) => Number((Number(n || 0)).toFixed(2));
const fmt2 = (n) =>
    new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
        Number(n || 0)
    );
const toCurrency = (v) => fmt2(v) + " €";

const safeJSON = (x, fb = {}) => {
    try {
        return typeof x === "string" ? JSON.parse(x) : x ?? fb;
    } catch {
        return fb;
    }
};
const pxNum = (n, fb) => (Number.isFinite(Number(n)) ? Number(n) : fb);

const API_ORIGIN = (() => {
    const b = axiosInstance?.defaults?.baseURL || "http://localhost:5000/api";
    try {
        return new URL(b).origin;
    } catch {
        return "http://localhost:5000";
    }
})();
const getAuthToken = () => {
    const auth = axiosInstance?.defaults?.headers?.common?.Authorization;
    if (auth?.startsWith("Bearer ")) return auth.slice(7);
    try {
        return (
            localStorage.getItem("token") ||
            localStorage.getItem("authToken") ||
            localStorage.getItem("jwt") ||
            ""
        );
    } catch {
        return "";
    }
};
const withToken = (p) => {
    if (!p) return p;
    const u = new URL(p, API_ORIGIN);
    const t = getAuthToken();
    if (t) u.searchParams.set("t", t);
    return u.toString();
};

const MM_PER_PX = 25.4 / 96;
const pxToMm = (px) => Math.round(Number(px || 0) * MM_PER_PX * 100) / 100;
const boxStyle = (isPdf, box = {}, z = 1) => {
    const { x, y, width, height } = box;
    if (!isPdf)
        return {
            position: "absolute",
            left: `${x}px`,
            top: `${y}px`,
            ...(width != null ? { width: `${width}px` } : {}),
            ...(height != null ? { height: `${height}px` } : {}),
            zIndex: z,
            pointerEvents: "none",
            background: "transparent",
        };
    return {
        position: "absolute",
        left: `${pxToMm(x)}mm`,
        top: `${pxToMm(y)}mm`,
        ...(width != null ? { width: `${pxToMm(width)}mm` } : {}),
        ...(height != null ? { height: `${pxToMm(height)}mm` } : {}),
        zIndex: z,
        pointerEvents: "none",
        background: "transparent",
    };
};

const dFR = (raw) => {
    if (!raw) return "-";
    const s = typeof raw === "string" ? raw.slice(0, 10) : new Date(raw).toISOString().slice(0, 10);
    const [y, m, d] = s.split("-");
    return `${d}/${m}/${y}`;
};
const toYYYYMMDD = (raw) => {
    const d = raw ? new Date(raw) : new Date();
    if (Number.isNaN(d.getTime())) return "";
    const y = d.getFullYear(),
        m = String(d.getMonth() + 1).padStart(2, "0"),
        day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};

const totalLigne = (l) =>
    parseFloat(l.quantite ?? l.qte ?? 0) * parseFloat(l.prixUnitaire ?? l.pu ?? 0) || 0;
const toNum = (v) => {
    if (typeof v === "number") return Number.isFinite(v) ? v : 0;
    if (typeof v === "string") {
        const s = v.replace(/\s/g, "").replace(",", ".");
        const n = Number(s);
        return Number.isFinite(n) ? n : 0;
    }
    return 0;
};
const normalizeType = (f) => String(f.type || f.type_facture || f.kind || "").toLowerCase();

// Pure (pas de variable externe)
const totalLigneAvecRemise = (l, idx, remises = []) => {
    const q = Number(l.quantite || 0);
    const pu = Number(l.prixUnitaire || 0);
    const base = q * pu;

    const list = (remises || []).filter((r) => r.rowIndex === idx);
    if (!list.length) return base;

    let res = base;
    for (const r of list) {
        if (r.type === "percent") {
            const pct = Math.min(100, Math.max(0, Number(r.value || 0)));
            res = res * (1 - pct / 100);
        } else if (r.type === "amount") {
            res = res - Number(r.value || 0);
        }
    }
    return Math.max(0, Number.isFinite(res) ? res : 0);
};

/* ---------- Paiement / Échéance ---------- */
const addDaysISO = (iso, days) => {
    const d = new Date(iso);
    if (!Number.isNaN(d.getTime())) d.setDate(d.getDate() + Number(days || 0));
    return d.toISOString().slice(0, 10);
};
const endOfMonthISO = (iso) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    d.setMonth(d.getMonth() + 1, 0);
    return d.toISOString().slice(0, 10);
};
const parsePaymentTerms = (html) => {
    const text = String(html || "").replace(/<[^>]+>/g, " ");
    const aReception = /à\s*r[eé]ception/i.test(text);
    const finDeMois = /fin\s*de\s*mois/i.test(text);
    const mDays = text.match(/(\d{1,3})\s*jours?/i);
    const days = mDays ? Math.min(365, parseInt(mDays[1], 10)) : 30;
    return { aReception, finDeMois, days };
};
const computeDueDateISO = (issueISO, terms) => {
    if (terms?.aReception) return issueISO;
    if (terms?.finDeMois) return addDaysISO(endOfMonthISO(issueISO), terms.days || 0);
    return addDaysISO(issueISO, terms?.days || 30);
};

/* ---------- Adresse client ---------- */
const splitClientAddr = (obj = {}) => {
    const addrRaw =
        (obj.client_adresse || obj.numero_voie || obj.voie || obj.rue || obj.adresse || "").trim();
    const m = addrRaw.match(/^(.*?)(?:,\s*)?(\d{4,5})\s+(.+)$/i);
    const ligne1 = (m ? m[1] : addrRaw).trim();
    const cp = (obj.code_postal || obj.cp || obj.postal_code || (m ? m[2] : "")).trim();
    const ville = (obj.ville || obj.commune || obj.city || (m ? m[3] : "")).trim();
    return { ligne1, cp, ville };
};

/* ---------- Infos projet ---------- */
const getProjectInfo = (devis) => {
    const pidCandidates = [
        devis?.projet_id,
        devis?.project_id,
        devis?.id_projet,
        devis?.projet?.id,
        devis?.project?.id,
    ]
        .map(Number)
        .filter(Number.isFinite);
    const pid = pidCandidates.length ? pidCandidates[0] : null;

    const pname = String(
        devis?.projet_nom ??
        devis?.projet ??
        devis?.titre_projet ??
        devis?.project_name ??
        devis?.projet?.nom ??
        devis?.project?.name ??
        ""
    ).trim();

    return { pid, pname };
};

/* ---------- utils pour l’email ---------- */
function blobToBase64(blob) {
    return new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onloadend = () => resolve(String(r.result).split(",")[1] || "");
        r.onerror = reject;
        r.readAsDataURL(blob);
    });
}

function ActionsEmailFactureLikeDevis({ meta, pdfElRef, footerElRef }) {
    // meta: { id, numero, client_email, societe_nom }
    const kind = "finale";
    const [open, setOpen] = React.useState(false);

    const [to, setTo] = React.useState(meta?.client_email || "");
    const [cc, setCc] = React.useState("");

    const [tpls, setTpls] = React.useState({
        initial: {
            subject: "Votre facture {{numero}}",
            body: "Bonjour,\nVeuillez trouver ci-joint votre facture {{numero}}.\n\nCordialement,\n{{societe_nom}}",
        },
        relance: {
            subject: "Relance de paiement – facture {{numero}}",
            body: "Bonjour,\nSauf erreur de notre part, la facture {{numero}} reste en attente.\n\nMerci de votre règlement,\n{{societe_nom}}",
        },
    });

    const [subject, setSubject] = React.useState("");
    const [message, setMessage] = React.useState("");
    const [mode, setMode] = React.useState("initial");
    const [attachPdf, setAttachPdf] = React.useState(true);

    const storageKey = `mailHistory:facture:${kind}:${meta?.id || "temp"}`;
    const [history, setHistory] = React.useState(() => {
        try { return JSON.parse(localStorage.getItem(storageKey) || "[]"); } catch { return []; }
    });
    const [banner, setBanner] = React.useState(null);

    const values = React.useMemo(() => ({
        numero: meta?.numero || "",
        societe_nom: meta?.societe_nom || "FlowDevis",
    }), [meta?.numero, meta?.societe_nom]);

    const renderTpl = (s = "", v = {}) =>
        String(s).replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k) => v[k] ?? "");

    const fmtDateTime = (d) => {
        try { return new Intl.DateTimeFormat("fr-FR", { dateStyle: "long", timeStyle: "short" }).format(d); }
        catch { return d?.toLocaleString?.() || String(d); }
    };
    const emailLooksValid = (s = "") => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s).trim());

    // mappe sur les IDs (comme ActionsEmailDevis)
    const templateIdForInitial = "facture_finale";

    async function fetchTemplates() {
        try {
            const r = await axiosInstance.get("/email-templates");
            const d = r?.data || {};
            const get = (id) => {
                const t = d[id] || {};
                return { subject: t.subject || "", body: t.html || "" };
            };
            const initial = get(templateIdForInitial);
            const relance = get("relance_impaye");

            if (!initial.subject && !initial.body) {
                initial.subject = `Votre facture {{numero}} – {{societe_nom}}`;
                initial.body = `Bonjour,\nVeuillez trouver ci-joint votre facture {{numero}}.\n\nCordialement,\n{{societe_nom}}`;
            }
            if (!relance.subject && !relance.body) {
                relance.subject = `Relance de paiement – facture {{numero}}`;
                relance.body = `Bonjour,\nSauf erreur de notre part, la facture {{numero}} reste en attente.\n\nMerci de votre règlement,\n{{societe_nom}}`;
            }
            return { initial, relance };
        } catch {
            return null;
        }
    }

    React.useEffect(() => {
        if (!open) return;
        (async () => {
            try {
                const fromApi = await fetchTemplates();
                if (fromApi?.initial || fromApi?.relance) {
                    setTpls((o) => ({ ...o, ...fromApi }));
                }
            } finally {
                const tpl = mode === "relance" ? tpls.relance : tpls.initial;
                setSubject(renderTpl(tpl.subject, values));
                setMessage(renderTpl(tpl.body, values));
            }

            try {
                const r = await axiosInstance.get("/emails/history", {
                    params: { type: "facture", id: Number(meta?.id), kind },
                });
                const fromServer = Array.isArray(r?.data) ? r.data : [];
                const merged = [...fromServer, ...history].sort((a, b) => new Date(b.ts) - new Date(a.ts));
                setHistory(merged.slice(0, 100));
                try { localStorage.setItem(storageKey, JSON.stringify(merged.slice(0, 100))); } catch { }
            } catch { }

            setBanner(null);
            setTo(meta?.client_email || "");
        })();
    }, [open]);

    React.useEffect(() => {
        if (!open) return;
        const tpl = mode === "relance" ? tpls.relance : tpls.initial;
        setSubject(renderTpl(tpl.subject, values));
        setMessage(renderTpl(tpl.body, values));
    }, [mode, tpls.initial.subject, tpls.initial.body, tpls.relance.subject, tpls.relance.body]);

    async function buildPdfAttachment() {
        if (!attachPdf) return null;
        const rootEl = pdfElRef?.current ?? document.querySelector(".a4-page");
        const fEl = footerElRef?.current ?? document.querySelector(".a4-footer");
        if (!rootEl) throw new Error("a4-page introuvable");

        document.body.classList.add("static-pdf");
        rootEl.classList.add("pdf-export");
        await new Promise((r) => setTimeout(r, 20));

        const filename = `Facture-Finale-${(meta?.numero || meta?.id || "document")}.pdf`;
        const blob = await buildPdfBlobWithLastFooterAndPages(rootEl, fEl, { filename });
        const contentBase64 = await (async () => {
            const reader = new FileReader();
            const p = new Promise((res, rej) => {
                reader.onloadend = () => res(String(reader.result).split(",")[1] || "");
                reader.onerror = rej;
            });
            reader.readAsDataURL(blob);
            return p;
        })();

        rootEl.classList.remove("pdf-export");
        document.body.classList.remove("static-pdf");

        return { filename, contentBase64 };
    }

    async function send() {
        try {
            const email = String(to || "").trim();
            const ccStr = String(cc || "").trim();
            if (!emailLooksValid(email)) {
                notify.warning({ title: "Adresse e-mail invalide", message: "Renseigne une adresse valide dans « À »." });
                return;
            }
            const tpl = mode === "relance" ? tpls.relance : tpls.initial;
            const subj = subject?.trim() || renderTpl(tpl.subject, values);
            const body = message?.trim() || renderTpl(tpl.body, values);

            notify.info({ title: "Préparation", message: "Génération du PDF…" });
            const attachment = await buildPdfAttachment();

            await axiosInstance.post("/emails/send-document", {
                type: "facture",
                id: Number(meta?.id) || undefined,
                kind,
                to: email,
                cc: ccStr || undefined,
                subject: subj,
                message: body,
                attachment,
                meta: { mode },
            });

            const ts = new Date();
            setBanner(`✉️ ${mode === "relance" ? "Relance envoyée" : "E-mail envoyé"} le ${fmtDateTime(ts)}.`);

            const entry = { ts: ts.toISOString(), mode, to: email, cc: ccStr || "", subject: subj };
            const next = [entry, ...history].slice(0, 100);
            setHistory(next);
            try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch { }

            try { await axiosInstance.post("/emails/log", { type: "facture", id: Number(meta?.id), kind, entry }); } catch { }

            notify.success({ title: "E-mail envoyé", message: `Facture ${values.numero} envoyée à ${email}.` });
        } catch (err) {
            console.error("[send-finale-email]", err);
            notify.error({ title: "Échec de l’envoi", message: "Impossible d’envoyer l’e-mail. Réessaie dans un instant." });
        } finally {
            document.body.classList.remove("static-pdf");
            pdfElRef?.current?.classList?.remove?.("pdf-export");
        }
    }

    const sendBtnLabel = mode === "relance" ? "Envoyer une relance" : "Envoyer la facture";

    return (
        <>
            <BoutonOnglet actif={false} onClick={() => setOpen(true)}>Envoyer par e-mail</BoutonOnglet>

            {open && (
                <div className="fixed inset-0 z-[2000] grid place-items-center bg-sky-800/40">
                    <div className="bg-white rounded-xl shadow-xl p-5 w-[900px] max-w-[95vw]">
                        <div className="mb-4 rounded bg-slate-50 border border-slate-200 p-2 text-xs text-slate-700 max-h-24 overflow-auto">
                            <div className="font-semibold mb-1">Historique des envois – {values.numero} (finale)</div>
                            {history.length === 0 ? (
                                <div>Aucun envoi enregistré.</div>
                            ) : (
                                history.map((h, i) => (
                                    <div key={i} className="flex justify-between gap-3 py-0.5">
                                        <span>{new Date(h.ts).toLocaleString()}</span>
                                        <span>{h.mode === "relance" ? "Relance" : "Initial"} → {h.to}{h.cc ? ` (Cc: ${h.cc})` : ""}</span>
                                        <span className="truncate max-w-[45%]" title={h.subject}>{h.subject}</span>
                                    </div>
                                ))
                            )}
                        </div>

                        {banner && (
                            <div className="mb-4 rounded-md border border-green-300 bg-green-50 text-green-800 px-3 py-2 text-sm">
                                {banner}
                            </div>
                        )}

                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold">Envoyer la facture finale</h3>
                            <div className="inline-flex rounded-full bg-slate-100 p-1 text-sm">
                                <button type="button" className={`px-3 py-1 rounded-full ${mode === "initial" ? "bg-white shadow" : "opacity-70"}`} onClick={() => setMode("initial")}>
                                    Facture initiale
                                </button>
                                <button type="button" className={`px-3 py-1 rounded-full ${mode === "relance" ? "bg-white shadow" : "opacity-70"}`} onClick={() => setMode("relance")}>
                                    Relance facture
                                </button>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm mb-1">À</label>
                                <input className="w-full border rounded px-3 py-2 mb-3" value={to} onChange={(e) => setTo(e.target.value)} />
                                <label className="block text-sm mb-1">Cc (optionnel)</label>
                                <input className="w-full border rounded px-3 py-2 mb-3" value={cc} onChange={(e) => setCc(e.target.value)} />
                                <label className="block text-sm mb-1">Objet</label>
                                <input className="w-full border rounded px-3 py-2 mb-3" value={subject} onChange={(e) => setSubject(e.target.value)} />
                                <label className="block text-sm mb-1">Message</label>
                                <textarea className="w-full border rounded px-3 py-2 h-48" value={message} onChange={(e) => setMessage(e.target.value)} />
                                <label className="inline-flex items-center gap-2 mt-3">
                                    <input type="checkbox" checked={attachPdf} onChange={(e) => setAttachPdf(e.target.checked)} />
                                    Joindre le PDF
                                </label>
                            </div>

                            <div className="border rounded p-3 bg-slate-50">
                                <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">
                                    Modèle utilisé : {mode === "relance" ? "Relance facture" : "Facture initiale"} (finale)
                                </div>
                                <div className="text-sm font-semibold mb-2">
                                    {renderTpl((mode === "relance" ? tpls.relance.subject : tpls.initial.subject), values)}
                                </div>
                                <pre className="text-xs whitespace-pre-wrap leading-5">
                                    {renderTpl((mode === "relance" ? tpls.relance.body : tpls.initial.body), values)}
                                </pre>
                            </div>
                        </div>

                        <div className="flex gap-2 justify-end mt-5">
                            <button type="button" className="px-3 py-2 border rounded" onClick={() => setOpen(false)}>Fermer</button>
                            <button type="button" className="px-4 py-2 bg-sky-700 text-white rounded" onClick={send}>
                                {sendBtnLabel}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

function MentionsBlock({ html, fontPx = 12 }) {
    if (!html) return null;
    return (
        <div
            className="mentions-legales"
            style={{ fontSize: `${fontPx}px`, lineHeight: 1.5 }}
            dangerouslySetInnerHTML={{ __html: html }}
        />
    );
}

/* ---------------- Page principale ---------------- */
export default function FactureFinale() {
    const { infosSociete } = useContext(SocieteContext);
    const [params] = useSearchParams();
    const navigate = useNavigate();

    const devisId =
        params.get("devisId") || params.get("devis") || params.get("devis_id") || params.get("devisid");

    /* -------- states -------- */
    const [devis, setDevis] = useState(null);
    const [blocHtmlSafe, setBlocHtmlSafe] = useState("");
    const [mentionsHtml, setMentionsHtml] = useState("");
    const [mentionsFontPx, setMentionsFontPx] = useState(12);
    const [remisesLignes, setRemisesLignes] = useState([]);
    const [siblings, setSiblings] = useState([]);
    const [eligible, setEligible] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);

    const [finalId, setFinalId] = useState(null);
    const [finalNumero, setFinalNumero] = useState(null);
    const [finalDate, setFinalDate] = useState(toYYYYMMDD());
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const [isPdf, setIsPdf] = useState(false);
    const pdfRef = useRef(null);
    const footerRef = useRef(null);
    // ← theme tableau (lis + réagit aux changements, comme sur Devis/CréerFacture)
    const [tableTheme, setTableTheme] = useState(loadTableTheme());
    useEffect(() => {
        const onFocus = () => setTableTheme(loadTableTheme());
        window.addEventListener("focus", onFocus);
        const off = onThemeChange(setTableTheme);
        return () => { window.removeEventListener("focus", onFocus); off?.(); };
    }, []);
    // Snapshot pour isDirty
    const initialSnapshotRef = useRef(null);
    const currentSnapshot = useMemo(
        () => ({
            devisId: Number(devisId) || null,
            finalDate: String(finalDate || ""),
            selectedIds: [...selectedIds].sort((a, b) => a - b),
            remisesLignes: (remisesLignes || []).map((r) => ({
                rowIndex: r.rowIndex,
                type: r.type,
                value: r.value,
                label: r.label,
            })),
        }),
        [devisId, finalDate, selectedIds, remisesLignes]
    );

    useEffect(() => {
        // reset quand devis chargé / finale créée/màj
        initialSnapshotRef.current = JSON.stringify(currentSnapshot);
    }, [devis?.id, finalId, finalNumero]); // ajoute d’autres deps si besoin

    const isDirty = useMemo(() => {
        const a = initialSnapshotRef.current || "";
        const b = JSON.stringify(currentSnapshot);
        return a !== b;
    }, [currentSnapshot]);

    const ownerKey = infosSociete?.user_id || infosSociete?.id || infosSociete?.siret || "no-owner";
    useEffect(() => {
        revokeAllProtectedUrls();
    }, [ownerKey]);

    /* --------- Chargements --------- */
    useEffect(() => {
        if (!devisId) return;
        (async () => {
            try {
                const { data } = await axiosInstance.get(`/devis/${devisId}`);
                setDevis(data);
            } catch (e) {
                console.error("Erreur chargement devis:", e);
                setError("Impossible de charger le devis.");
            }
        })();
    }, [devisId]);

    useEffect(() => {
        (async () => {
            const raw = (infosSociete?.bloc_html || "").trim();
            const clean = DOMPurify.sanitize(raw);
            const hydrated = await hydrateProtectedHtml(clean);
            setBlocHtmlSafe(hydrated);
        })();
    }, [infosSociete?.bloc_html]);

    useEffect(() => {
        (async () => {
            try {
                let data;
                try {
                    data = (await axiosInstance.get("/mentionsfacturation")).data || {};
                } catch {
                    data = (await axiosInstance.get("/mentionslegales")).data || {};
                }
                const raw =
                    data?.mentions_facturation ??
                    data?.mentions_supplementaires_facture ??
                    data?.mentions_supplementaires ??
                    "";
                const clean = DOMPurify.sanitize((raw || "").trim());
                const hydrated = await hydrateProtectedHtml(clean);
                setMentionsHtml(hydrated.replace(/<\/?p[^>]*>/gi, ""));
                const px = Number(data?.mentions_font_px);
                setMentionsFontPx(Number.isFinite(px) ? px : 12);
            } catch {
                setMentionsHtml("");
                setMentionsFontPx(12);
            }
        })();
    }, []);

    /* ---------- Récup factures liées ---------- */
    useEffect(() => {
        if (!devisId) return;
        let cancelled = false;

        const idNum = Number(devisId);

        const pickArray = (payload) => {
            if (Array.isArray(payload)) return payload;
            if (Array.isArray(payload?.data)) return payload.data;
            if (Array.isArray(payload?.items)) return payload.items;
            return [];
        };

        const commonSizes = {
            limit: 1000,
            per_page: 1000,
            page_size: 1000,
            pageSize: 1000,
            "page[size]": 1000,
            "pagination[pageSize]": 1000,
        };

        const { pid, pname } = getProjectInfo(devis || {});
        const numDevisStr = String(devis?.numero_devis || devis?.numero || "").trim();

        const tries = [
            { url: "/factures", params: { devis_id: idNum, ...commonSizes } },
            { url: "/factures", params: { devisId: idNum, ...commonSizes } },
            { url: "/factures", params: { devis: idNum, ...commonSizes } },
            { url: "/factures", params: { "filters[devis_id][$eq]": idNum, ...commonSizes } },
            { url: "/factures", params: { "filter[devis_id]": idNum, ...commonSizes } },
            { url: `/devis/${idNum}/factures`, params: { ...commonSizes } },
            ...(pid
                ? [
                    { url: "/factures", params: { projet_id: pid, ...commonSizes } },
                    { url: "/factures", params: { project_id: pid, ...commonSizes } },
                    { url: "/factures", params: { "filters[projet_id][$eq]": pid, ...commonSizes } },
                    { url: "/factures", params: { "filter[projet_id]": pid, ...commonSizes } },
                ]
                : []),
            { url: "/factures", params: { ...commonSizes } },
        ];

        const parseMeta = (f) => {
            try {
                return typeof f.meta === "string" ? JSON.parse(f.meta) : f.meta || {};
            } catch {
                return {};
            }
        };

        const relatesToDevisOrProject = (f) => {
            const meta = parseMeta(f);

            const devids = [
                f.devis_id,
                f.devisId,
                f.devis,
                meta.devis_id,
                meta.devisId,
                meta.source_devis_id,
                meta.devis,
            ]
                .map(Number)
                .filter(Number.isFinite);
            if (devids.some((v) => v === idNum)) return true;

            const pfids = [
                f.projet_id,
                f.project_id,
                f.id_projet,
                meta.projet_id,
                meta.project_id,
                meta.id_projet,
            ]
                .map(Number)
                .filter(Number.isFinite);
            if (pid && pfids.some((v) => v === pid)) return true;

            const textRefs = [
                f.devis_numero,
                f.numero_devis,
                meta.devisRef,
                meta.devis_ref,
                meta.devisNumero,
                meta.devis_number,
            ]
                .filter(Boolean)
                .map(String);
            if (numDevisStr && textRefs.some((s) => s === numDevisStr)) return true;

            const pnames = [f.projet_nom, f.project_name, meta.projetNom, meta.projectName]
                .filter(Boolean)
                .map((s) => String(s).trim().toLowerCase());
            if (pname && pnames.includes(String(pname).toLowerCase())) return true;

            try {
                const cont = Array.isArray(f.contenu) ? f.contenu : JSON.parse(f.contenu || "[]");
                if (Array.isArray(cont)) {
                    const joined = JSON.stringify(cont).toLowerCase();
                    if (numDevisStr && joined.includes(numDevisStr.toLowerCase())) return true;
                    if (String(idNum) && joined.includes(String(idNum))) return true;
                    if (pname && joined.includes(String(pname).toLowerCase())) return true;
                }
            } catch { }
            return false;
        };

        (async () => {
            let collected = [];
            for (const t of tries) {
                try {
                    const { data } = await axiosInstance.get(t.url, t.params ? { params: t.params } : undefined);
                    const arr = pickArray(data);
                    if (arr.length) {
                        collected = arr;
                        if (t !== tries[tries.length - 1]) break;
                    }
                } catch {
                    /* next */
                }
            }

            const filtered = (collected || []).filter((f) => {
                const t = normalizeType(f);
                if (!(t.includes("acompte") || t.includes("partiel") || t === "finale")) return false;
                return relatesToDevisOrProject(f);
            });

            if (!cancelled) setSiblings(filtered);
        })();

        return () => {
            cancelled = true;
        };
    }, [devisId, devis?.numero_devis, devis?.numero, devis?.projet_id, devis?.projet_nom]);

    /* ---------- Finale existante + éligibles ---------- */
    useEffect(() => {
        const list = Array.isArray(siblings) ? siblings : [];

        const finale = list.find((f) => normalizeType(f) === "finale");
        if (finale) {
            setFinalId(finale.id);
            setFinalNumero(finale.numero || finale.numero_facture || null);
            setFinalDate(toYYYYMMDD(finale.date || finale.date_facture || finale.created_at || new Date()));
            const preSel = safeJSON(finale.meta)?.selected_facture_ids || [];
            const pre = (preSel || []).map(Number).filter(Boolean);
            if (pre.length) setSelectedIds(pre);
        }

        const elig = list
            .filter((f) => {
                const t = normalizeType(f);
                if (!(t.includes("acompte") || t.includes("partiel"))) return false;
                const etat = String(f.etat || f.status || "").toLowerCase();
                if (etat.includes("annul") || etat.includes("cancel")) return false;
                const meta = safeJSON(f.meta);
                const ttc = toNum(f.montant_ttc) || toNum(f.total_ttc) || toNum(meta.montant_ttc) || 0;
                return ttc > 0;
            })
            .sort(
                (a, b) =>
                    new Date(a.date || a.date_facture || a.created_at || 0) -
                    new Date(b.date || b.date_facture || b.created_at || 0)
            );

        setEligible(elig);
        if (!finale) setSelectedIds(elig.map((f) => Number(f.id)));
    }, [siblings]);

    /* -------- Lignes & totaux du devis -------- */
    const lignes = useMemo(() => {
        if (!devis) return [];
        try {
            return Array.isArray(devis.contenu) ? devis.contenu : JSON.parse(devis.contenu || "[]");
        } catch {
            return [];
        }
    }, [devis]);

    const totauxDevis = useMemo(() => {
        const ht = lignes.reduce((s, l, i) => s + totalLigneAvecRemise(l, i, remisesLignes), 0);
        const tva = lignes.reduce((s, l, i) => {
            const base = totalLigneAvecRemise(l, i, remisesLignes);
            return s + base * (parseFloat(l.tva || 0) / 100);
        }, 0);
        return { ht: r2(ht), tva: r2(tva), ttc: r2(ht + tva) };
    }, [lignes, remisesLignes]);

    /* -------- Total déductions sélectionnées -------- */
    const deductionTTC = useMemo(() => {
        const set = new Set(selectedIds.map(Number));
        return r2(
            eligible
                .filter((f) => set.has(Number(f.id)))
                .reduce((s, f) => {
                    const meta = safeJSON(f.meta);
                    const ttc =
                        toNum(f.montant_ttc) || toNum(f.total_ttc) || toNum(meta.montant_ttc) || 0;
                    return s + ttc;
                }, 0)
        );
    }, [eligible, selectedIds]);

    /* -------- Montants finale -------- */
    const finaleHT = useMemo(
        () => r2(Math.max(totauxDevis.ht - deductionTTC, 0)),
        [totauxDevis.ht, deductionTTC]
    );
    const finaleTVA = useMemo(() => r2(totauxDevis.tva), [totauxDevis.tva]);
    const finaleTTC = useMemo(
        () => r2(Math.max(totauxDevis.ttc - deductionTTC, 0)),
        [totauxDevis.ttc, deductionTTC]
    );

    /* -------- Paiement / Échéance -------- */
    const terms = useMemo(() => parsePaymentTerms(mentionsHtml), [mentionsHtml]);
    const echeanceISO = computeDueDateISO(finalDate, terms);
    const paymentLeadLabel = terms.aReception
        ? "à réception"
        : terms.finDeMois
            ? `${terms.days} jours fin de mois`
            : `${terms.days} jours`;

    /* -------- Footer collé -------- */
    useEffect(() => {
        const apply = () => {
            if (!footerRef.current || !pdfRef.current) return;
            const h = footerRef.current.offsetHeight || 0;
            pdfRef.current.style.setProperty("--footer-h", `${h}px`);
        };
        apply();
        const ro = new ResizeObserver(apply);
        if (footerRef.current) ro.observe(footerRef.current);
        window.addEventListener("resize", apply);
        return () => {
            ro.disconnect();
            window.removeEventListener("resize", apply);
        };
    }, [mentionsHtml, isPdf]);

    /* ---------- Apparence devis (logo + bloc texte) ---------- */
    const posTexteRaw = safeJSON(infosSociete?.positiontexte, infosSociete?.positiontexte);
    const posTexte = {
        x: pxNum(posTexteRaw?.x, 40),
        y: pxNum(posTexteRaw?.y, 120),
        width: pxNum(posTexteRaw?.width, 320),
        height: pxNum(posTexteRaw?.height, null),
    };

    const placementsRaw = safeJSON(infosSociete?.logos_on_page, infosSociete?.logos_on_page) || [];
    const logosOnPage = (Array.isArray(placementsRaw) ? placementsRaw : []).map((p) => {
        const w = pxNum(p.width, 140);
        const h = p.height != null ? pxNum(p.height, 70) : Math.round(w * 0.5);
        const rawSrc = p?.src || (p?.file_id ? `/api/files/${p.file_id}` : null);
        return {
            id: p.id,
            x: pxNum(p.x, 420),
            y: pxNum(p.y, 40),
            width: w,
            height: h,
            src: rawSrc ? withToken(rawSrc) : null,
        };
    });

    const monoLogo =
        !logosOnPage.length && infosSociete?.logo && infosSociete?.positionlogo
            ? {
                x: pxNum(safeJSON(infosSociete.positionlogo)?.x, 420),
                y: pxNum(safeJSON(infosSociete.positionlogo)?.y, 40),
                width: pxNum(safeJSON(infosSociete.positionlogo)?.width, 140),
                height:
                    safeJSON(infosSociete.positionlogo)?.height != null
                        ? pxNum(safeJSON(infosSociete.positionlogo)?.height, 70)
                        : Math.round(pxNum(safeJSON(infosSociete.positionlogo)?.width, 140) * 0.5),
                src: withToken(String(infosSociete.logo || "")),
            }
            : null;

    /* -------- Save (PUT si finale existe) -------- */
    const ensureSingleFinale = async () => {
        if (finalId) return finalId;
        try {
            const { data } = await axiosInstance.get("/factures", {
                params: { devis_id: Number(devisId) },
            });
            const arr = Array.isArray(data) ? data : [];
            const exist = arr.find((f) => normalizeType(f) === "finale");
            if (exist) {
                setFinalId(exist.id);
                setFinalNumero(exist.numero || exist.numero_facture || null);
                setFinalDate(toYYYYMMDD(exist.date || exist.date_facture || exist.created_at || new Date()));
                return exist.id;
            }
        } catch { }
        return null;
    };

    const doSave = async () => {
        if (!devisId || !devis?.client_id) {
            setError("Devis ou client manquant.");
            return null;
        }
        setSaving(true);
        setError("");
        try {
            const already = await ensureSingleFinale();
            const payload = {
                type: "finale",
                devis_id: Number(devisId),
                client_id: Number(devis.client_id),
                date: finalDate,
                date_facture: finalDate,
                total_ht: finaleHT,
                total_tva: finaleTVA,
                total_ttc: finaleTTC,
                montant_ht: finaleHT,
                montant_tva: finaleTVA,
                montant_ttc: finaleTTC,
                contenu: JSON.stringify(lignes || []),
                intro_texte: devis?.intro_texte || "",
                projet_nom:
                    devis?.projet_nom ||
                    devis?.projet ||
                    devis?.titre_projet ||
                    `${devis?.client_nom ? devis.client_nom + " — " : ""}Devis ${devis?.numero_devis || devis?.numero || devis?.id
                    }`,
                mode_calcul: "reste",
                valeur: undefined,
                meta: JSON.stringify({
                    base: { totalHT: totauxDevis.ht, totalTVA: totauxDevis.tva, totalTTC: totauxDevis.ttc },
                    selected_facture_ids: selectedIds.map(Number),
                    selected_deduction_ttc: deductionTTC,
                    devisRef: devis?.numero_devis || devis?.numero || devis?.id,
                    kind: "finale",
                }),
            };

            if (already || finalId) {
                const id = already || finalId;
                const { data } = await axiosInstance.put(`/factures/${id}`, payload, {
                    headers: { "Content-Type": "application/json" },
                });
                setFinalNumero(data?.numero || data?.numero_facture || finalNumero);
                setFinalId(id);
                notify.success({
                    title: "Mise à jour",
                    message: `Facture ${data?.numero || data?.numero_facture || ""} mise à jour.`,
                });
                try {
                    initialSnapshotRef.current = JSON.stringify(currentSnapshot);
                } catch { }
                return data;
            } else {
                const { data } = await axiosInstance.post(`/factures`, payload, {
                    headers: { "Content-Type": "application/json" },
                });
                setFinalId(data?.id);
                setFinalNumero(data?.numero || data?.numero_facture || null);
                notify.success({
                    title: "Enregistrée",
                    message: `Facture ${data?.numero || data?.numero_facture || ""} sauvegardée.`,
                });
                try {
                    initialSnapshotRef.current = JSON.stringify(currentSnapshot);
                } catch { }
                return data;
            }
        } catch (e) {
            console.error("Erreur enregistrement facture finale :", e);
            setError(e?.response?.data?.message || "Erreur lors de l'enregistrement");
            notify.error({ title: "Échec", message: "L’enregistrement a échoué." });
            return null;
        } finally {
            setSaving(false);
        }
    };
    // Modale "changements non sauvegardés"
    const [unsavedOpen, setUnsavedOpen] = useState(false);
    const nextHrefRef = useRef(null);

    // Alerte native si on ferme/recharge l’onglet
    useEffect(() => {
        const handler = (e) => {
            if (!isDirty) return;
            e.preventDefault();
            e.returnValue = "";
            return "";
        };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [isDirty]);

    // Demander la sortie vers une route
    const requestLeaveTo = useCallback((to) => {
        if (!isDirty) {
            navigate(to);
            return;
        }
        nextHrefRef.current = to;
        setUnsavedOpen(true);
    }, [isDirty, navigate]);

    // Handlers UnsavedDialog
    const onUnsavedCancel = useCallback(() => {
        setUnsavedOpen(false);
        nextHrefRef.current = null;
    }, []);

    const onUnsavedExit = useCallback(() => {
        const target = nextHrefRef.current || "/documents";
        setUnsavedOpen(false);
        nextHrefRef.current = null;
        navigate(target);
    }, [navigate]);

    const onUnsavedSaveAndExit = useCallback(async () => {
        try {
            await doSave();
        } finally {
            const target = nextHrefRef.current || "/documents";
            setUnsavedOpen(false);
            nextHrefRef.current = null;
            navigate(target);
        }
    }, [doSave, navigate]);

    /* -------- Export PDF (nouvelle version avec utilitaire commun) -------- */
    const exportPDF = async () => {
        // 1) on sauvegarde / met à jour la facture finale avant d’exporter
        const saved = await doSave?.();

        const rootEl = pdfRef.current;
        const footerEl = footerRef.current;
        if (!rootEl) return;

        const filename =
            `facture-finale-${saved?.numero ||
            saved?.numero_facture ||
            finalNumero ||
            devis?.numero_devis ||
            devis?.numero ||
            devisId
            }.pdf`;

        try {
            // active le mode "impression" (comme sur AfficherDevis / AfficherFacture)
            setIsPdf(true);
            document.body.classList.add("static-pdf");
            rootEl.classList.add("pdf-export");

            // petit délai pour laisser le DOM se mettre à jour
            await new Promise((r) => setTimeout(r, 20));

            // 2) génération du PDF avec footer propre sur la dernière page
            const blob = await buildPdfBlobWithLastFooterAndPages(rootEl, footerEl, { filename });

            // 3) téléchargement du PDF
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(url), 4000);
        } catch (e) {
            console.error("[exportPDF finale]", e);
            notify.error({
                title: "Export PDF",
                message: "Impossible de générer la facture en PDF pour le moment.",
            });
        } finally {
            rootEl.classList.remove("pdf-export");
            document.body.classList.remove("static-pdf");
            setIsPdf(false);
        }
    };


    const isReady = Boolean(devis && infosSociete);
    const { ligne1: addr1, cp, ville } = splitClientAddr(devis || {});
    const cpVille = [cp, ville].filter(Boolean).join(" ").trim();

    return (
        <div className="p-4 space-y-4">
            {/* Styles A4 + footer collé */}
            <style>{`
  .actions-bar :is(button, a){
    padding:10px 16px;
    line-height:1.2;
    font-size:.875rem;      /* text-sm */
    border-radius:9999px;   /* pill */
  }
`}</style>



            {!isReady ? (
                <div className="p-6 text-center">Chargement…</div>
            ) : (
                <>
                    {/* Barre d’actions */}
                    <div className="text-sm text-gray-700 mb-2">
                        {finalNumero ? (
                            <>Facture finale <strong>n°&nbsp;{finalNumero}</strong> — pour le devis <strong>{devis.numero_devis || devis.numero}</strong></>
                        ) : (
                            <>Facture finale (<em>n° à l’enregistrement</em>) — pour le devis <strong>{devis.numero_devis || devis.numero}</strong></>
                        )}
                    </div>

                    <div className="actions-bar flex flex-wrap justify-center gap-2 mb-4">
                        <ActionsEmailFactureLikeDevis
                            meta={{
                                id: finalId || null,
                                numero: finalNumero || "",
                                client_email: devis?.client_email || "",
                                societe_nom: infosSociete?.nom || infosSociete?.societe_nom || "FlowDevis",
                            }}
                            pdfElRef={pdfRef}
                            footerElRef={footerRef}
                        />
                        <BoutonOnglet onClick={exportPDF}>Exporter en PDF</BoutonOnglet>
                        <BoutonOnglet onClick={doSave}>{saving ? "Enregistrement…" : "Enregistrer"}</BoutonOnglet>
                        <BoutonOnglet onClick={() => requestLeaveTo("/documents")}>Retour aux documents</BoutonOnglet>                    </div>
                    {error && <div className="text-red-600 text-sm">{error}</div>}

                    {/* Sélecteur des déductions */}
                    <div className="border rounded p-3 text-sm">
                        <div className="font-semibold mb-2">Déduire des factures (acompte/partielle) du devis</div>
                        {eligible.length === 0 ? (
                            <div className="text-gray-600">Aucune facture éligible trouvée pour ce devis.</div>
                        ) : (
                            <>
                                <div className="flex justify-end gap-2 mb-2">
                                    <button
                                        className="px-3 py-1 text-sm rounded bg-gray-200 hover:bg-gray-300 text-gray-800 transition"
                                        onClick={() => setSelectedIds(eligible.map((f) => Number(f.id)))}
                                    >
                                        Tout cocher
                                    </button>
                                    <button
                                        className="px-3 py-1 text-sm rounded bg-gray-200 hover:bg-gray-300 text-gray-800 transition"
                                        onClick={() => setSelectedIds([])}
                                    >
                                        Tout décocher
                                    </button>
                                </div>

                                <div className="max-w-[740px] overflow-x-auto">
                                    <table className="w-full text-sm border">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="p-2 border w-10" />
                                                <th className="p-2 border text-left">Facture</th>
                                                <th className="p-2 border text-left">Date</th>
                                                <th className="p-2 border text-left">Type</th>
                                                <th className="p-2 border text-right">Montant TTC</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {eligible.map((f) => {
                                                const idNum = Number(f.id);
                                                const checked = selectedIds.includes(idNum);
                                                const numero = f.numero || f.numero_facture || `FAC-${idNum}`;
                                                const dt = f.date || f.date_facture || f.created_at;
                                                const meta = safeJSON(f.meta);
                                                const ttc =
                                                    toNum(f.montant_ttc) || toNum(f.total_ttc) || toNum(meta.montant_ttc) || 0;
                                                const t = normalizeType(f);
                                                return (
                                                    <tr key={idNum}>
                                                        <td className="p-2 border text-center">
                                                            <input
                                                                type="checkbox"
                                                                checked={checked}
                                                                onChange={(e) =>
                                                                    setSelectedIds((old) =>
                                                                        e.target.checked
                                                                            ? [...new Set([...old, idNum])]
                                                                            : old.filter((x) => x !== idNum)
                                                                    )
                                                                }
                                                            />
                                                        </td>
                                                        <td className="p-2 border">{numero}</td>
                                                        <td className="p-2 border">{dFR(dt)}</td>
                                                        <td className="p-2 border capitalize">{t}</td>
                                                        <td className="p-2 border text-right">{fmt2(ttc)} €</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="mt-2 text-gray-700">
                                    Total à déduire sélectionné : <strong>{fmt2(deductionTTC)} € TTC</strong>
                                </div>
                            </>
                        )}
                    </div>

                    <UnsavedDialog
                        open={unsavedOpen}
                        onCancel={onUnsavedCancel}
                        onExit={onUnsavedExit}
                        onSaveAndExit={onUnsavedSaveAndExit}
                    />

                    {/* APERÇU A4 */}
                    <div
                        className="a4-page relative"
                        ref={pdfRef}
                        style={{ ...(typeof themeStyleVars === "function" ? themeStyleVars(tableTheme) : {}) }}
                    >
                        {/* LOGO + bloc texte */}
                        <div key={isPdf ? "pdf" : "screen"}>
                            {blocHtmlSafe && (
                                <div style={boxStyle(isPdf, posTexte, 45)}>
                                    <div
                                        className="ql-editor !p-0 !leading-snug"
                                        style={{ overflow: "visible", maxHeight: "none" }}
                                        dangerouslySetInnerHTML={{ __html: blocHtmlSafe }}
                                    />
                                </div>
                            )}

                            {logosOnPage.length > 0 &&
                                logosOnPage.map((pl, idx) => (
                                    <div
                                        key={pl.id || idx}
                                        data-logo
                                        style={{
                                            ...boxStyle(isPdf, { x: pl.x, y: pl.y, width: pl.width, height: pl.height }, 80),
                                            backgroundImage: pl.src ? `url("${pl.src}")` : undefined,
                                            backgroundRepeat: "no-repeat",
                                            backgroundPosition: "center",
                                            backgroundSize: "contain",
                                        }}
                                    />
                                ))}

                            {!logosOnPage.length && monoLogo && (
                                <div
                                    data-logo
                                    style={{
                                        ...boxStyle(
                                            isPdf,
                                            { x: monoLogo.x, y: monoLogo.y, width: monoLogo.width, height: monoLogo.height },
                                            80
                                        ),
                                        backgroundImage: monoLogo.src ? `url("${monoLogo.src}")` : undefined,
                                        backgroundRepeat: "no-repeat",
                                        backgroundPosition: "center",
                                        backgroundSize: "contain",
                                    }}
                                />
                            )}
                        </div>
                        {/* Bloc client */}
                        <div className="absolute z-20 top-[150px] right-[40px] min-w-[300px] max-w-[500px] bg-white border border-gray-300 shadow-md rounded p-4 text-sm">
                            <h2 className="text-md font-semibold mb-2">{devis?.client_nom || "—"}</h2>
                            <div className="text-gray-700 space-y-1">
                                {addr1 && <p>{addr1}</p>}
                                {cpVille && <p>{cpVille}</p>}
                            </div>
                        </div>

                        {/* En-tête */}
                        <div className="absolute left-[40px] top-[280px] w-[360px] text-sm z-[100]">
                            <div className="font-bold whitespace-nowrap">
                                Facture finale {finalNumero ? <>n°&nbsp;{finalNumero}</> : "(n° à l’enregistrement)"} — devis&nbsp;
                                {devis?.numero_devis || devis?.numero}
                            </div>

                            <div className="mt-1 flex items-center gap-2">
                                <div className="text-xs text-gray-700">Émise le {dFR(finalDate)}</div>
                                {!isPdf &&
                                    !(typeof document !== "undefined" && document.body.classList.contains("static-pdf")) && (
                                        <button
                                            type="button"
                                            className="no-print text-xs text-sky-700 hover:underline"
                                            title="Modifier la date"
                                            onClick={() => {
                                                const v = prompt("Date (YYYY-MM-DD)", finalDate);
                                                if (v) {
                                                    try {
                                                        new Date(v).toISOString();
                                                        setFinalDate(v);
                                                    } catch { }
                                                }
                                            }}
                                        >
                                            Modifier
                                        </button>
                                    )}
                            </div>
                        </div>

                        {/* Contenu */}
                        <div className="a4-content mt-[320px] px-6">
                            {devis?.intro_texte?.trim() && (
                                <div className="text-sm italic text-gray-700 mb-3">
                                    <div
                                        className="ql-editor !p-0"
                                        dangerouslySetInnerHTML={{ __html: devis.intro_texte }}
                                    />
                                </div>
                            )}

                            <div className="mt-1">
                                <TableauStyleVertical
                                    lignes={lignes}
                                    lectureSeule
                                    totalLigne={(l, idx) => totalLigneAvecRemise(l, idx, remisesLignes)}
                                    remisesLignes={remisesLignes}
                                    onRemisesLignesChange={setRemisesLignes}
                                />
                            </div>

                            <div className="mt-6 flex justify-end">
                                <div className="w-[360px] border-t">
                                    <div className="flex justify-between py-2 text-sm border-b">
                                        <span>Sous-total HT</span>
                                        <span>{toCurrency(totauxDevis.ht)}</span>
                                    </div>

                                    {eligible
                                        .filter((f) => selectedIds.includes(Number(f.id)))
                                        .map((f) => {
                                            const numero = f.numero || f.numero_facture || `FAC-${f.id}`;
                                            const dt = f.date || f.date_facture || f.created_at;
                                            const meta = safeJSON(f.meta);
                                            const ttc =
                                                toNum(f.montant_ttc) ||
                                                toNum(f.total_ttc) ||
                                                toNum(meta.montant_ttc) ||
                                                0;
                                            const t = normalizeType(f);
                                            return (
                                                <div
                                                    key={f.id}
                                                    className="flex justify-between py-2 text-xs border-b text-gray-700"
                                                >
                                                    <span>
                                                        {t.includes("acompte") ? "Acompte" : "Facture partielle"}{" "}
                                                        {numero} du {dFR(dt)}
                                                    </span>
                                                    <span>- {toCurrency(ttc)}</span>
                                                </div>
                                            );
                                        })}

                                    {Array.isArray(remisesLignes) && remisesLignes.length > 0 && (
                                        <>
                                            {remisesLignes.map((r, k) => {
                                                const numero = (r.rowIndex ?? 0) + 1;
                                                const l = lignes[r.rowIndex] || {};
                                                const base = Number((l.quantite || 0) * (l.prixUnitaire || 0));
                                                let delta = 0;
                                                if (r.type === "percent") {
                                                    delta =
                                                        base -
                                                        base *
                                                        (1 -
                                                            Math.min(
                                                                100,
                                                                Math.max(0, Number(r.value || 0))
                                                            ) /
                                                            100);
                                                } else if (r.type === "amount") {
                                                    delta = Math.min(base, Number(r.value || 0));
                                                }
                                                return (
                                                    <div
                                                        key={k}
                                                        className="flex justify-between py-2 text-xs border-b text-gray-700"
                                                    >
                                                        <span>
                                                            Remise désignation N°{numero}
                                                            {r.label ? ` — ${r.label}` : ""} (
                                                            {r.type === "percent"
                                                                ? `${r.value}%`
                                                                : `${fmt2(r.value)} €`}
                                                            )
                                                        </span>
                                                        <span>- {toCurrency(r2(delta))}</span>
                                                    </div>
                                                );
                                            })}
                                        </>
                                    )}

                                    <div className="flex justify-between py-2 text-sm border-b">
                                        <span>Total HT</span>
                                        <span>{toCurrency(finaleHT)}</span>
                                    </div>
                                    <div className="flex justify-between py-2 text-sm border-b">
                                        <span>TVA</span>
                                        <span>{toCurrency(finaleTVA)}</span>
                                    </div>
                                    <div className="flex justify-between py-2 text-base font-semibold">
                                        <span>Total TTC</span>
                                        <span>{toCurrency(finaleTTC)}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="text-right mt-4">
                                <div className="text-lg font-bold">
                                    Net à payer : {toCurrency(finaleTTC)}
                                </div>
                            </div>

                            {mentionsHtml && (
                                <div className="mt-4 text-gray-700 border-t pt-2 no-split">
                                    <p className="mb-2" style={{ fontSize: `${mentionsFontPx}px` }}>
                                        <strong>Délai de paiement :</strong> {paymentLeadLabel} —{" "}
                                        <strong>Échéance le {dFR(echeanceISO)}.</strong>
                                    </p>
                                    <MentionsBlock html={mentionsHtml} fontPx={mentionsFontPx} />
                                </div>
                            )}
                        </div>

                        {/* ✅ Footer séparé, comme sur AfficherFacture */}
                        <div className="a4-footer" ref={footerRef}>
                            <FooterApercu />
                        </div>

                    </div>
                </>
            )}
        </div>
    );
}
